import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import {
  normalizeBaseGist,
  normalizeGist,
  normalizePageData,
} from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { IAllPageFilters, IBaseGist, IGist, IPageType } from 'utils/types';

import { prepareFormData } from '../../utils/form';

import { normalizePsp, normalizePspDetails } from './normalizers';
import {
  ICopyPspParameterPayload,
  IPsp,
  IPspDetails,
  IPspForm,
  IPspParameterForm,
  IPspParameterPayload,
  TPspData,
} from './types';

const pspAPI = {
  async getAllPsp(filters: IAllPageFilters): Promise<IPageType<IPsp>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('banks', {
      params: normalizedFilter,
    });

    return normalizePageData<TPspData, IPsp>(
      response.data,
      normalizePsp,
      JSON.stringify(filters.search),
    );
  },

  async getPsp(id: string): Promise<IPspDetails> {
    const response = await axiosInstance.get(`banks/${id}`);

    return normalizePspDetails(response.data);
  },

  async getUserPsp(): Promise<IGist[]> {
    const response = await axiosInstance.get(`banks/names`);

    return response.data?.result?.map(normalizeGist) || [];
  },

  async createPsp(payload: IPspForm): Promise<IBaseGist> {
    const response = await axiosInstance.post(`banks`, payload);

    return normalizeBaseGist(response?.data);
  },

  async updatePsp({ id, ...payload }: IPspForm): Promise<IPspDetails> {
    const response = await axiosInstance.put(`banks/${id}`, payload);

    return normalizePspDetails(response.data);
  },

  async deletePsp(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`banks/${id}`);

    return response.data;
  },

  async deletePspParameter(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`banks/parameters/${id}`);

    return response;
  },

  async createPspParameter({
    id,
    payload,
  }: IPspParameterPayload): Promise<AxiosResponse> {
    const normalizedParameter = prepareFormData(payload);
    const result = await axiosInstance.post(
      `banks/${id}/parameters`,
      normalizedParameter,
    );
    return result.data;
  },

  async updatePspParameter({
    id: parameterId,
    ...payload
  }: IPspParameterForm): Promise<AxiosResponse> {
    const normalizedParameter = prepareFormData(payload);
    const result = await axiosInstance.put(
      `banks/parameters/${parameterId}`,
      normalizedParameter,
    );
    return result.data;
  },

  async copyPspParameter({
    id,
    payload,
  }: ICopyPspParameterPayload): Promise<AxiosResponse> {
    const normalizedParameter = prepareFormData(payload);
    const result = await axiosInstance.patch(
      `banks/${id}/parameters`,
      normalizedParameter,
    );
    return result.data;
  },
};

export default pspAPI;
